// This file contains helper components for displaying product restrictions in a table format.
// It includes a component for displaying the current and recommended thresholds.
// The components are styled using Material-UI and custom CSS.

import React, { useState, useEffect, useId, useRef, useContext } from 'react';
import {
    Box, Chip, Tooltip, Checkbox,
    LinearProgress
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import "./style.css";
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { RestrictionsMainDataContext, RowDataContext, RestrictionsStateContext } from './contexts';
import { callApiDirect, useFetchApi } from '../../../hooks/useFetchApi';
import icons from './svgIcons';
import { getEffectedDates, toggleBucketsRecRestriction, updateNewBucketData, hasAllDatesRecAccepted, isRestrictionEdited, isAnyRestrictionsAltered, sortRoomSummary, cleanBucketsData } from './functions';
import { apiListAndData } from "../../../app/apiListAndData";
import _ from 'lodash';
import moment from 'moment';
// import analytics from '../../../app/analytics-config';
// import { analytics } from '../../hooks/use-analytics';
import { analytics } from '../../../hooks/use-analytics';
import { toast } from 'react-toastify';
import { deepCopy, userReadyOnlyStatus } from '../../../app/util';
import useUserLoginType from '../../../hooks/useUserLoginType';

export const ColumnSorting = (props) => {
    const { label, colItem, sorted, onOrderByChange } = props;
    const { sortUpActive, sortDownActive, sortUp, sortDown } = icons;
    const onClickHandler = () => {
        if (!sorted) {
            colItem.sorted = 'asc';
        }
        if (sorted === 'asc') {
            colItem.sorted = 'desc';
        }
        if (sorted === 'desc') {
            colItem.sorted = null;
        }
        onOrderByChange(colItem);
    }

    return (
        <Tooltip title={"Sort by " + label} arrow>
            <Box className="sorting-icons" onClick={onClickHandler}>
                {colItem.sorted === 'asc' ? sortUpActive : sortUp}
                {colItem.sorted === 'desc' ? sortDownActive : sortDown}
            </Box>
        </Tooltip>
    )
}

export function ThresholdCheckBox() {
    const restrictionsState = useContext(RestrictionsStateContext);
    const restrictionsMainData = useContext(RestrictionsMainDataContext);
    /** accept threshhold checkbox functionality  */
    const [isThresholdChecked, setIsThresholdChecked] = useState(0);
    const { app_state, ui_state } = useSelector((state) => state.app.current_state);
    const asOfDate = app_state?.as_of_date;
    // let [payload, setPayload] = useState([]);

    const thresholdAcceptedChecked = (e) => {
        const checked = e.target.checked;

        let mainData = _.cloneDeep(restrictionsMainData?.data);
        let payload = { "asOfDate": asOfDate, data: { "stayDates": [] }, actionType: (checked) ? "threshold_accepted" : "threshold_rejected" };

        mainData.forEach(item => {
            const isPastDate = moment(item.date).isBefore(moment().format("YYYY-MM-DD"));

            if (!isPastDate) {
                if (item.threshold.recommended) {
                    item.threshold.curr_user_action = checked ? 'recommended_accepted' : '';
                }

                if (item.threshold.recommended) {
                    payload.data.stayDates.push(item.date);
                }

                if (item?.buckets?.length) {
                    item.buckets = toggleBucketsRecRestriction(item.buckets, checked);
                    item.bucket_update += item.bucket_update;
                }
            }
        });
        setIsThresholdChecked(checked);
        restrictionsMainData.updateData(mainData);
        const res = callApiDirect(app_state, "thresholdToggle", payload, 'PUT');
        setTimeout(() => {
            restrictionsState.updateState({ thresholdCheckedStatus: checked, updateFrom: "mainChkbox" });
        }, 300)

    };
    /** accept threshhold checkbox functionality  */

    useEffect(() => {
        const allRecAccepted = hasAllDatesRecAccepted(restrictionsMainData?.data);
        if (allRecAccepted) {
            setIsThresholdChecked(allRecAccepted);
            restrictionsState.state.thresholdCheckedStatus = allRecAccepted;
            restrictionsState.updateState({ thresholdCheckedStatus: allRecAccepted, updateFrom: "mainChkbox" });
        }
    }, [])

    useEffect(() => {
        if (restrictionsState.state.updateFrom && restrictionsState.state.updateFrom === "dateThreshold") {
            setIsThresholdChecked(restrictionsState.state.thresholdCheckedStatus);
        }
    }, [restrictionsState.state.thresholdCheckedStatus, restrictionsState.state.updateFrom])

    return (
        <>
            <Checkbox
                checked={isThresholdChecked}
                icon={icons.unCheckedIcon2}
                checkedIcon={icons.checkedIcon2}
                indeterminateIcon={icons.indeterminateIcon2}
                sx={{ p: 0, ml: "2px", mb: "2px", height: "16px", width: "20px" }}
                onChange={thresholdAcceptedChecked}
            />
        </>
    )
}

export const BadgeCountDisplay = () => {
    const rowData = useContext(RowDataContext);
    let badgeCounts = rowData?.data?.badge_counts;
    let recAccepted = () => rowData?.data?.threshold?.curr_user_action === 'recommended_accepted';
    let [displayValue, setDisplayValue] = useState(badgeCounts.user_updated);
    useEffect(() => {
        if (rowData?.data?.threshold?.curr_user_action == 'recommended_accepted') {
            badgeCounts.user_updated = badgeCounts.rec_accepted;
            displayValue = badgeCounts.user_updated;
        }
        if (rowData?.data?.threshold?.curr_user_action == 'overridden') {
            displayValue = badgeCounts.user_updated;
        }
        if (rowData?.data?.threshold?.curr_user_action == '') {
            badgeCounts.user_updated = badgeCounts.initial;
            displayValue = badgeCounts.user_updated;
        }
        setDisplayValue(displayValue);
    }, [rowData?.data?.threshold?.curr_user_action, badgeCounts.user_updated]);

    return (
        <div className='badge-count'>{displayValue}</div>
    );
}

export const ThresholdDisplay = (props) => {
    const dispatch = useDispatch();
    const restrictionsState = useContext(RestrictionsStateContext);
    const { recalcStatus } = restrictionsState.state;
    const mainData = useContext(RestrictionsMainDataContext);
    const rowData = useContext(RowDataContext);
    const indexOfRow = mainData?.data?.findIndex(item => item.date === rowData?.data?.date);
    mainData.data[indexOfRow] = rowData?.data;

    const appState = useSelector((state) => state);
    const { current_property_date } = appState?.auth?.profile;
    const { app_state, ui_state } = appState?.app?.current_state;
    const { plrFeatures, as_of_date } = app_state;
    const { currency_symbol } = ui_state;

    // rowData.data.isRestrictionsOverridden = true;
    const { threshold, badge_counts, restriction_updated, date, details_open, isRestrictionsOverridden, processing } = rowData.data;
    let initValue = threshold.user;
    if (threshold.user === threshold.recommended) initValue = threshold.recommended;
    if (threshold.user === threshold.current) initValue = threshold.current;
    const [displayValue, setDisplayValue] = useState(initValue);
    const [recommendedAccepted, setRecommendedAccepted] = useState(threshold.recommended_accepted);
    const id = useId();
    const thresholdInput = useRef(null);
    const asOfDate = app_state?.as_of_date;

    const compRef = useRef({});

    const isPastDate = moment(date).isBefore(moment().format("YYYY-MM-DD"));
    const isPastAsOfDate = moment(as_of_date).isBefore(current_property_date);


    const userLoginType = useUserLoginType();
    const usrReadyOnlyStatus = userReadyOnlyStatus(userLoginType);

    useEffect(() => {

        if (!isPastDate && restrictionsState?.state?.updateFrom && restrictionsState?.state?.updateFrom === "mainChkbox") {
            if (restrictionsState?.state?.thresholdCheckedStatus) {
                handleAcceptRecommended();
            } else {
                handleUndoAcceptRecommended();
            }
            rowData.data.processing = false;
            rowData.updateRowData({ processing: false });
        }
    }, [restrictionsState.state.thresholdCheckedStatus]);

    //Updating cart count when any restiction changed in details view
    useEffect(() => {
        if (restriction_updated) {
            if (app_state && app_state.plrDatesToPush && !app_state.plrDatesToPush.includes(date)) {
                const newPlrDatesToPush = app_state.plrDatesToPush;
                newPlrDatesToPush.push(date);

            }
        } else {
            if (app_state && app_state.plrDatesToPush && app_state.plrDatesToPush.includes(date)) {
                // debugger;
                // const newPlrDatesToPush = app_state.plrDatesToPush;
                // newPlrDatesToPush.push(date);
                // dispatch({
                //     type: "UPDATE_STATE",
                //     payload: {
                //         app_state: {
                //             plrDatesToPush: newPlrDatesToPush,
                //         }
                //     }
                // });
            }
        }
    }, [restriction_updated]);


    useEffect(() => {
        if (!isPastDate) {
            // console.log("threshold date:", date, rowData.data);
            const isOverridden = threshold.current != threshold.user;
            if (!isOverridden) {
                setRecommendedAccepted(false);
                setDisplayValue(threshold.user);
                if (thresholdInput?.current) {
                    thresholdInput.current.value = threshold.user;
                    thresholdInput.current.classList.remove('edited-threshold');
                }
            }
            if (threshold?.curr_user_action === "recommended_accepted") {
                setRecommendedAccepted(true);
                setDisplayValue(threshold.recommended);
                if (thresholdInput?.current) {
                    thresholdInput.current.value = threshold.recommended;
                    thresholdInput.current.classList.add('edited-threshold');
                }
            }
            if (isOverridden) {
                setRecommendedAccepted(false);
                setDisplayValue(threshold.user);
                if (thresholdInput?.current) {
                    thresholdInput.current.value = threshold.user;
                    thresholdInput.current.classList.add('edited-threshold');
                }
            }
        }

    }, [
        // rowData?.data?.threshold?.curr_user_action, 
        rowData?.data?.threshold.user
    ])

    const handleAcceptRecommended = (apiCall = false) => {
        if (rowData.data.processing || !thresholdInput.current) return;
        // Logic to handle accepting the recommended threshold
        rowData.data.processing = true;
        setRecommendedAccepted(true);
        threshold.user = threshold.recommended;
        setDisplayValue(threshold.recommended);
        thresholdInput.current.value = threshold.recommended;
        thresholdInput.current.classList.add('edited-threshold');
        threshold.recommended_accepted_ = true;
        // threshold.is_overridden = false;
        threshold.curr_user_action = "recommended_accepted";
        rowData.updateRowData({ processing: true });
        // restrictionsState.updateState({ rowThresholdToggled: moment().format("x") });
        //Making buckets rec accepted if data available
        if (rowData.data.buckets && rowData.data.buckets.length) {
            let buckets = _.cloneDeep(toggleBucketsRecRestriction(rowData.data.buckets, true));
            rowData.updateRowData({ buckets: buckets });
            let indexOfRow = mainData.data.findIndex(item => item.date === rowData.data.date);
            mainData.data[indexOfRow].buckets = buckets;
        }


        // send API call to shubham to tell rec accepted
        if (apiCall) {

            let payload = { "asOfDate": asOfDate, data: { "stayDates": [rowData?.data?.date] }, actionType: "threshold_accepted" };
            // tmp.threshold.recommended_accepted = true;
            // tmp.threshold.current = threshold.current;
            // tmp.threshold.recommended = threshold.recommended;
            const res = callApiDirect(app_state, "thresholdToggle", payload, 'PUT');
            res.then((rs) => {
                rowData.data.processing = false;
                rowData.data.rowUpdated = "recAccepted_" + moment().format("x");
                rowData.updateRowData({ processing: false, rowUpdated: "recAccepted_" + moment().format("x") });
            });
        }

        if (!restrictionsState.state.thresholdCheckedStatus) {
            let isAllNotRecAccepted = mainData?.data.some((item) => {
                const isPastDate = moment(item.date).isBefore(moment().format("YYYY-MM-DD"))
                if (!isPastDate && !(item.threshold.recommended_accepted || !item.threshold.recommended)) {
                    return !item.threshold.recommended_accepted_
                }
            })
            if (!isAllNotRecAccepted) {
                restrictionsState.updateState({ thresholdCheckedStatus: true, updateFrom: "dateThreshold" });
            }
        }

        let plrDatesToPush = _.cloneDeep(app_state.plrDatesToPush); //getEffectedDates({ mainData: mainData.data, app_state });
        let indexOfDate = plrDatesToPush.indexOf(date);
        if (indexOfDate < 0) plrDatesToPush.push(date);
        dispatch({
            type: "UPDATE_STATE",
            payload: {
                app_state: {
                    plrDatesToPush: plrDatesToPush //getEffectedDates({ mainData: mainData.data }),
                }
            }
        });
    }

    const handleUndoAcceptRecommended = (apiCall = false) => {
        if (rowData.data.processing || !thresholdInput.current) return;
        // Logic to handle undo the recommended threshold

        setRecommendedAccepted(false);
        threshold.user = threshold.current;
        setDisplayValue(threshold.user);
        thresholdInput.current.value = threshold.user;
        thresholdInput.current.classList.remove('edited-threshold');
        threshold.recommended_accepted_ = false;
        // threshold.is_overridden = false;
        threshold.curr_user_action = "";

        rowData.updateRowData({ threshold: _.cloneDeep(threshold) });

        if (apiCall) {
            rowData.data.processing = true;
            rowData.updateRowData({ processing: true });
            // send API call to tell rec undo
            let payload = { "asOfDate": asOfDate, data: { "stayDates": [rowData?.data?.date] }, actionType: "threshold_rejected" };
            const res = callApiDirect(app_state, "thresholdToggle", payload, 'PUT');
            res.then((rs) => {
                rowData.data.processing = false;
                rowData.updateRowData({ processing: false });

                if (restrictionsState.state.thresholdCheckedStatus) {
                    restrictionsState.updateState({ thresholdCheckedStatus: false, updateFrom: "dateThreshold" });
                }

                //Making buckets rec accepted if data available
                if (rowData.data.buckets && rowData.data.buckets.length) {
                    rowData.data.buckets = toggleBucketsRecRestriction(rowData.data.buckets, false);
                    rowData.updateRowData({ buckets: rowData.data.buckets });
                }

                restrictionsState.updateState({ rowThresholdToggled: moment().format("x") });

            });
        }

        let plrDatesToPush = _.cloneDeep(app_state.plrDatesToPush); //getEffectedDates({ mainData: mainData.data, app_state });
        let indexRemove = plrDatesToPush.indexOf(date);
        if (indexRemove > -1) plrDatesToPush.splice(indexRemove, 1);

        dispatch({
            type: "UPDATE_STATE",
            payload: {
                app_state: {
                    plrDatesToPush: plrDatesToPush //getEffectedDates({ mainData: mainData.data }),
                }
            }
        });
    }

    const removeFromApiProgress = (dateToRemove) => {
        const key = "apiprogress";
        const existing = sessionStorage.getItem(key);
        const progressArray = existing ? JSON.parse(existing) : [];
        const updated = progressArray.filter(item => item !== dateToRemove);
        sessionStorage.setItem(key, JSON.stringify(updated));
        window.dispatchEvent(new Event("sessionStorageChange"));
    };

    const addToApiProgress = (newItem) => {
        const key = "apiprogress";
        const existing = sessionStorage.getItem(key);
        const progressArray = existing ? JSON.parse(existing) : [];
        progressArray.push(newItem);
        sessionStorage.setItem(key, JSON.stringify(progressArray));

        // 🔔 Trigger re-render in same tab
        window.dispatchEvent(new Event("sessionStorageChange"));
    };

    const checkThresholdValue = (e) => {
        if(usrReadyOnlyStatus) return false;
        
        let value = Number(e.target.value);
        // check value not negative or not empty or not string
        if (value < 0 || value === '' || isNaN(value)) {
            value = threshold.current;
            thresholdInput.current.value = value;
        }
        if (value !== compRef.current.prevThreshold) {
            threshold.user = value;
            threshold.curr_user_action = value === threshold.current ? "" : "overridden";
            // rowData.data.threshold.user = Number(value);
            setDisplayValue(value);
            thresholdInput.current.value = value;
            rowData.updateRowData({ threshold: threshold });

            if (value === threshold.recommended) {
                // setRecommendedAccepted(true);
                // setDisplayValue(value);
                // threshold.curr_user_action = "recommended_accepted";
                // rowData.data.rowUpdated = "recAccepted_" + moment().format("x");
                handleAcceptRecommended(true);
            }
            if (value == threshold.current) {
                // setRecommendedAccepted(true);
                // setDisplayValue(value);
                // threshold.curr_user_action = "recommended_accepted";
                // rowData.data.rowUpdated = "recAccepted_" + moment().format("x");
                //handleUndoAcceptRecommended(true); //commented for 1st phase release
            }

            if (
                value !== threshold.recommended
                //&& value !== threshold.current //commented for 1st phase release
            ) {
                addToApiProgress(date);
                rowData.data.processing = true;
                rowData.updateRowData({ processing: true });
                let payload = {
                    "asOfDate": asOfDate,
                    "data": {
                        "stayDates": [date],
                        "updatedThreshold": value
                    },
                    "actionType": "threshold_overridden",
                    "isDetailsOpen": rowData.data.details_open
                }
                if (value === threshold.recommended) {
                    payload = {
                        "asOfDate": asOfDate,
                        "data": {
                            "stayDates": [date],
                        },
                        "actionType": "threshold_accepted",
                        "is_details_open": rowData.data.details_open
                    }
                }
                if (rowData.data?.buckets?.length) {
                    const openedBuckets = [];
                    rowData.data?.buckets.forEach((bucket) => {
                        if (bucket.details_open) openedBuckets.push(bucket.label);
                    });
                    if (openedBuckets.length) {
                        payload.data.openedBuckets = openedBuckets;
                    }
                }


                const res = callApiDirect(app_state, "thresholdToggle", payload, 'PUT');
                res.then((rs) => {
                    if (rs.error) {
                        toast.dismiss();
                        toast.error("Threshold override failed", {
                            position: "top-right"
                        });
                        removeFromApiProgress(date);
                        analytics.track("plr_threshold_override_failed", {
                            selected_tile: ui_state.active_tiles.join(),
                            property_id: app_state.property_id,
                            property_name: app_state.property_name,
                            pid: app_state.property_id,
                            day_date: date,
                            override_success: false,
                            data_level: app_state.workspace_controls?.segmentType,
                        });
                        threshold.curr_user_action = "";
                        threshold.user = compRef.current.prevThreshold;
                        rowData.data.processing = false;
                        rowData.updateRowData({ processing: false, threshold: threshold });
                        throw rs.msg || rs.error;
                    }
                    removeFromApiProgress(date);
                    rowData.data.processing = false;
                    const rsData = rs?.data?.data?.data;
                    const currentDate = rsData?.find(item => moment(item.stay_date).format("YYYY-MM-DD") === date);
                    const datesEffected = [];
                    if (rsData && rsData.length && rsData.length > 1) {
                        rsData.forEach((item) => {
                            if (moment(item.stay_date).format("YYYY-MM-DD") !== date) {
                                datesEffected.push(item);
                            }
                        });
                    };

                    rowData.data.badge_counts.user_updated = currentDate?.badge_counts?.user_updated ?? rowData.data.badge_counts.user_updated;
                    let newBucketData = deepCopy(currentDate?.buckets) ?? null;
                    if (newBucketData) {
                        newBucketData = sortRoomSummary(newBucketData, props.selectedRoomTypes);
                        // if (rowData.data.details_open) {
                        rowData.data.buckets = newBucketData; //updateNewBucketData(rowData.data.buckets, newBucketData);
                        // }
                        const apiKeys = Object.keys(apiListAndData);
                        apiKeys.forEach((k) => {
                            if (k === 'getDateRestrictionDetails') apiListAndData[k].data = {};
                        })
                    }
                    threshold.curr_user_action = currentDate.threshold.curr_user_action; //"overridden";
                    rowData.data.rowUpdated = "overridden_" + moment().format("x");
                    rowData.updateRowData({
                        rowUpdated: rowData.data.rowUpdated,
                        threshold: deepCopy(currentDate.threshold),
                        badge_counts: deepCopy(currentDate.badge_counts),
                        buckets: newBucketData,
                        processing: false
                    });

                    restrictionsState.updateState({ datesToUpdate: datesEffected, thresholdCheckedStatus: false, updateFrom: "dateThreshold" });
                    //Added to update cart count correctly
                    const indexOfRow = mainData.data.findIndex(item => item.date === rowData.data.date);
                    mainData.data[indexOfRow] = rowData.data;

                    const plrDatesToPush = app_state.plrDatesToPush;
                    let isAnyRestrictionsChanged = isAnyRestrictionsAltered(rowData.data.buckets)
                    if (threshold.curr_user_action || isAnyRestrictionsChanged) {
                        if (!plrDatesToPush.includes(date)) {
                            plrDatesToPush.push(date);
                        }
                    }
                    if (!isAnyRestrictionsChanged) {
                        if (plrDatesToPush.includes(date)) {
                            const idx = plrDatesToPush.indexOf(date);
                            if(currentDate && currentDate?.threshold?.current === currentDate?.threshold?.user){
                                plrDatesToPush.splice(idx, 1);
                            }
                        }
                    }
                    dispatch({
                        type: "UPDATE_STATE",
                        payload: {
                            app_state: {
                                plrDatesToPush: plrDatesToPush, //getEffectedDates({ mainData: mainData.data, app_state }),
                            }
                        }
                    });

                }, (error) => {
                    console.error(error);
                    threshold.curr_user_action = "";
                    threshold.user = compRef.current.prevThreshold;
                    removeFromApiProgress(date);
                    toast.dismiss();
                    toast.error("Threshold override failed", {
                        position: "top-right"
                    });
                    rowData.updateRowData({ processing: false, threshold: threshold });
                })
            }

            // dispatch({
            //     type: "UPDATE_STATE",
            //     payload: {
            //         app_state: {
            //             plrDatesToPush: getEffectedDates({ mainData: mainData.data, app_state }),
            //         }
            //     }
            // });
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            let focusable = getCurThresholdElements();
            let index = focusable.indexOf(document.activeElement);
            if (index > -1 && index < focusable.length - 1) {
                focusable[index + 1].focus();
            }
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            let focusable = getCurThresholdElements();
            let index = focusable.indexOf(document.activeElement);
            if (index > 0) {
                focusable[index - 1].focus();
            }
        }
    }

    function getCurThresholdElements() {
        return Array.from(document.querySelectorAll(
            '.cur-threshold'
        )).filter(el => !el.disabled && el.offsetParent !== null);
    }

    const handleRecalculate = () => {
        // debugger;
        // rowData.data.recalculating = true;
        // rowData.updateRowData({ recalculating: true });
        // restrictionsState.updateState({ recalcStatus: { date: date, status: 'processing' } });
        let isAnyRestrictionsChanged = isAnyRestrictionsAltered(rowData.data.buckets)

        if (app_state.plrRowCalcStatus !== 'processing') {
            dispatch({
                type: "UPDATE_STATE",
                payload: {
                    app_state: {
                        plrRowCalcStatus: 'processing',
                    }
                }
            });
        }

        let isMinLosEdited = isRestrictionEdited(rowData.data.buckets, "MN");
        let payload = {
            "asOfDate": asOfDate,
            "data": {
                "stayDates": [date],
                // "updatedThreshold": value
                "buckets": cleanBucketsData(rowData.data.buckets),
                "isMinLosEdited": isMinLosEdited,
                "isEdited": isAnyRestrictionsChanged
            },
            "actionType": "restrictions_updated",
            "isDetailsOpen": true,
        }

        if (rowData.data?.buckets?.length) {
            const openedBuckets = [];
            rowData.data?.buckets.forEach((bucket) => {
                if (bucket.details_open) openedBuckets.push(bucket.label);
            });
            if (openedBuckets.length) {
                payload.data.openedBuckets = openedBuckets;
            }
        }

        callApiDirect(app_state, "overrideRequest", payload, 'PUT')
            .then((rs) => {
                if (rs?.error) {
                    dispatch({
                        type: "UPDATE_STATE",
                        payload: {
                            app_state: {
                                plrRowCalcStatus: 'done',
                                plrRecalcDate: null,
                            }
                        }
                    });
                    console.error('handleRecalculate', rs);
                    throw rs.msg || rs.error;
                }
                let resData = {};
                if (rs?.data?.data?.data && _.isArray(rs.data.data.data)) {
                    resData = _.cloneDeep(rs.data.data.data[0]);
                };
                // console.log('handleRecalculate', rs);
                // rowData.data.isRestrictionsOverridden = false;
                // rowData.data.recalculating = false;
                rowData.data.badge_counts.user_updated = resData?.badge_counts?.user_updated ?? resData?.badge_counts?.user_updated;
                // rowData.data.threshold = rs?.data?.data?.data?.threshold ?? _.cloneDeep(rs?.data?.data?.data?.threshold);
                rowData.data.threshold.curr_user_action = resData?.threshold?.curr_user_action;
                rowData.data.threshold.user = resData?.threshold ? resData.threshold.user : rowData.data.threshold.user;
                if(!rowData.data.details_open){
                    rowData.data.buckets = _.cloneDeep(resData?.buckets);
                }
                setDisplayValue(rowData.data.threshold.user);

                rowData.updateRowData({ threshold: _.cloneDeep(rowData.data.threshold), badge_counts: _.cloneDeep(rowData.data.badge_counts), 
                    buckets: rowData.data.buckets //_.cloneDeep(resData?.buckets) || [] 
                });
                // restrictionsState.updateState({ recalcStatus: { date: null, status: 'done' } });
                const apiKeys = Object.keys(apiListAndData);
                apiKeys.forEach((k) => {
                    if (k === 'getDateRestrictionDetails') apiListAndData[k].data = {};
                })

                const plrDatesToPush = app_state.plrDatesToPush;

                if (plrDatesToPush.includes(rowData.data.date)) {
                    if (!isAnyRestrictionsChanged) {
                        const idx = plrDatesToPush.indexOf(rowData.data.date);
                        plrDatesToPush.splice(idx, 1);
                        //Remove from DB 
                        const payload = {
                            "asOfDate": app_state.as_of_date,
                            "data": {
                                "stayDates": [
                                    rowData.data.date
                                ]
                            },
                            "actionType": "date_remove"
                        }
                        // const result = callApiDirect(app_state, "singleDateDelete", payload, 'PUT');
                    }
                } else if (!plrDatesToPush.includes(rowData.data.date)) {
                    plrDatesToPush.push(rowData.data.date);
                }

                dispatch({
                    type: "UPDATE_STATE",
                    payload: {
                        app_state: {
                            plrDatesToPush: plrDatesToPush,
                            plrRowCalcStatus: 'done',
                            plrRecalcDate: date,
                            plrRowCalcSuccess: moment().format("x"),
                        }
                    }
                });

                const { property_id, property_name, workspace_controls, primary_stay_range } = app_state;
                const formatted_stay_date = moment(date).format('YYYY-MM-DD');//dateFormat(moment(), "YYYY-MM-DD");
                window.analyze.track("plr_threshold_override", {
                    "selected_tile": ui_state.active_tiles.join(),
                    property_id, property_name,
                    pid: property_id,
                    day_date: formatted_stay_date,
                    override_success: true,
                    data_level: app_state.workspace_controls?.segmentType,
                }, {}, {
                    app_state: {
                        workspace_controls,
                    },
                    ui_state: {
                    }
                });

            }, (error) => {
                // restrictionsState.updateState({ recalcStatus: { date: null, status: 'done' } });
                dispatch({
                    type: "UPDATE_STATE",
                    payload: {
                        app_state: {
                            plrRowCalcStatus: 'done',
                            plrRecalcDate: null,
                        }
                    }
                });
                console.error('handleRecalculate', error);
            });
    }

    useEffect(() => {
        if (app_state.plrRecalcDate === date && app_state.plrRowCalcStatus === 'processing') {
            if (rowData?.data?.buckets?.length) {
                handleRecalculate();
            } else {
                dispatch({
                    type: "UPDATE_STATE",
                    payload: {
                        app_state: {
                            plrRowCalcStatus: 'done',
                            plrRecalcDate: null,
                        }
                    }
                });
            }

            // restrictionsState.updateState({ thresholdRecalc: null });
            // restrictionsState.updateState({ recalcStatus: { date: null, status: 'done' } });

        }
    }, [app_state.plrRowCalcStatus])

    const editedClass = threshold?.curr_user_action === 'overridden' ? "edited-threshold" : "";

    if (app_state.plrRecalcDate === date) {
        if (app_state.plrRowCalcStatus === 'processing') {
            return (<Box className='threshold-container recalc loader'>
                Recalculating...
            </Box>)
        } else if (app_state.plrRowCalcStatus === 'active') {
            return (
                <>
                    {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: 0 }} />} */}
                    <Box className='threshold-container recalc' title="Click to recalculate" onClick={() => {
                        dispatch({
                            type: "UPDATE_STATE",
                            payload: {
                                app_state: {
                                    plrRowCalcStatus: 'processing',
                                }
                            }
                        });
                    }}>
                        Recalculate
                        <Box className="rec-wrapper">
                            <span style={{ marginTop: 5 }} className='recalc-btn'>{recalculateIcon}</span>
                        </Box>
                    </Box>
                </>
            )
        }
    }
    // threshold.pre_user_action = 'overridden';

    if (!plrFeatures.thresholdRec) {
        if (isPastDate) {
            return <>
                {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: '4px' }} />} */}
                <Box className="threshold-container past-date">
                    <input ref={thresholdInput}
                        id={id} tabIndex={1}
                        // type='number'
                        disabled={true}
                        className="cur-threshold"
                        defaultValue={displayValue}
                        onKeyDown={(e) => handleKeyDown(e)}
                        onBlur={checkThresholdValue} />
                </Box>
            </>
        }
        if (isPastAsOfDate && !isPastDate) {
            return <>
                {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: '4px' }} />} */}
                <Box className="threshold-container past-date past-asof" sx={{ cursor: 'not-allowed' }}>
                    <Tooltip title="Please set the “As of date” to the current date to update the threshold value." arrow>
                        <input ref={thresholdInput}
                            id={id} tabIndex={1}
                            // type='number'
                            disabled={true}
                            className="cur-threshold"
                            defaultValue={displayValue}
                            onKeyDown={(e) => handleKeyDown(e)}
                            onBlur={checkThresholdValue} />
                    </Tooltip>
                </Box>
            </>
        }
        return <>
            {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: '4px' }} />} */}
            <Box className="threshold-container">
                <input ref={thresholdInput}
                    id={id} tabIndex={1}
                    // type='number'
                    // disabled={processing}
                    readOnly={processing}
                    className={`cur-threshold ${editedClass}`}
                    defaultValue={displayValue}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onFocus={(e) => {
                        compRef.current.prevThreshold = Number(e.currentTarget.value);
                        return e.target.select()
                    }}
                    onChange={(e) => {
                        const val = Number(e.currentTarget.value);

                        if (val !== threshold.current) {
                            thresholdInput.current.classList.add('edited-threshold');
                        } else if (val == threshold.current) {
                            thresholdInput.current.classList.remove('edited-threshold');
                        }
                    }}
                    onBlur={(e) => {
                        setTimeout(() => {
                            checkThresholdValue(e);
                        }, 200)
                    }}
                    autoComplete="off" />
            </Box>
        </>
    }

    if (moment(date).isBefore(moment().format("YYYY-MM-DD"))) {
        return <>
            {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: '4px' }} />} */}
            <Box className="threshold-container past-date">
                <input ref={thresholdInput}
                    id={id} tabIndex={1}
                    // type='number'
                    disabled={true}
                    className="cur-threshold"
                    defaultValue={displayValue}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onBlur={checkThresholdValue} />

                {!threshold.recommended ? null : <>
                    <Box className="rec-wrapper disabled">
                        <div className='rec-text'>Rec:</div>
                        <Chip
                            className='rec-threshold'
                            variant="outlined"
                            label={
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    {threshold.recommended}
                                    {threshold.recommended > threshold.current ? upIcon : downIcon}
                                </span>
                            }
                        />
                    </Box>
                </>}
            </Box>
        </>
    }
    return (
        <>
            {/* {processing && <LinearProgress sx={{ height: '2px', margin: '-5px', marginBottom: '4px' }} />} */}
            <Box className={`threshold-container ${threshold.pre_user_action === 'overridden' ? 'prev-overridden' : ''} ${threshold.pre_user_action === 'recommended_accepted' ? ' prev-rec-accepted' : ''}`}>
                <input ref={thresholdInput}
                    id={id} tabIndex={1}
                    // type='number'
                    disabled={processing}
                    className={`cur-threshold ${threshold.curr_user_action === 'overridden' ? 'edited-threshold' : ''}`}
                    defaultValue={displayValue}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onFocus={(e) => {
                        compRef.current.prevThreshold = Number(e.currentTarget.value);
                        return e.target.select()
                    }}
                    onBlur={checkThresholdValue} />
                {!threshold.recommended ? null : <>
                    {recommendedAccepted ?
                        <Tooltip title={currency_symbol + threshold.current} arrow >
                            <Box
                                className='undo-threshold'
                                onClick={() => { handleUndoAcceptRecommended(true) }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#606060" viewBox="0 0 256 256"><path d="M232,144a64.07,64.07,0,0,1-64,64H80a8,8,0,0,1,0-16h88a48,48,0,0,0,0-96H51.31l34.35,34.34a8,8,0,0,1-11.32,11.32l-48-48a8,8,0,0,1,0-11.32l48-48A8,8,0,0,1,85.66,45.66L51.31,80H168A64.07,64.07,0,0,1,232,144Z"></path></svg>
                            </Box>
                        </Tooltip>
                        :
                        <>
                            {threshold.recommended && threshold.pre_user_action !== 'recommended_accepted' &&
                                <Box className={`rec-wrapper ${processing ? 'disabled' : ''}`} onClick={() => { handleAcceptRecommended(true) }}>
                                    <div className='rec-text'>Rec:</div>
                                    <Chip
                                        // onClick={handleAcceptRecommended}
                                        className='rec-threshold'
                                        //label={threshold.recommended} 
                                        variant="outlined"
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                {threshold.recommended}
                                                {threshold.recommended > threshold.current ? upIcon : downIcon}
                                            </span>
                                        }
                                    />
                                </Box>
                            }
                        </>
                    }
                </>}
            </Box>
        </>

    )
};

const upIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="#333333" viewBox="0 0 256 256"><path d="M205.66,117.66a8,8,0,0,1-11.32,0L136,59.31V216a8,8,0,0,1-16,0V59.31L61.66,117.66a8,8,0,0,1-11.32-11.32l72-72a8,8,0,0,1,11.32,0l72,72A8,8,0,0,1,205.66,117.66Z"></path></svg>
);

const downIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="#333333" viewBox="0 0 256 256"><path d="M205.66,149.66l-72,72a8,8,0,0,1-11.32,0l-72-72a8,8,0,0,1,11.32-11.32L120,196.69V40a8,8,0,0,1,16,0V196.69l58.34-58.35a8,8,0,0,1,11.32,11.32Z"></path></svg>
);

const recalculateIcon = (<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="18" rx="9" fill="#EBEBEB" />
    <path d="M4.125 8.9997C4.12489 10.1828 4.59069 11.3183 5.42157 12.1605C6.25245 13.0027 7.38157 13.4838 8.56453 13.4997H8.625C9.77426 13.5026 10.8805 13.0628 11.7141 12.2716C11.7499 12.2377 11.7786 12.1971 11.7987 12.1522C11.8189 12.1072 11.8299 12.0587 11.8313 12.0094C11.8327 11.9602 11.8243 11.9111 11.8067 11.8651C11.7892 11.8191 11.7627 11.777 11.7288 11.7412C11.695 11.7054 11.6544 11.6766 11.6094 11.6565C11.5644 11.6364 11.5159 11.6253 11.4667 11.624C11.4174 11.6226 11.3684 11.6309 11.3224 11.6485C11.2763 11.6661 11.2342 11.6926 11.1984 11.7264C10.6623 12.2321 9.98902 12.5685 9.26277 12.6937C8.53651 12.819 7.78945 12.7274 7.11492 12.4305C6.44039 12.1336 5.86829 11.6446 5.47009 11.0245C5.07189 10.4043 4.86525 9.6806 4.87597 8.9437C4.88669 8.20681 5.1143 7.48941 5.53038 6.88112C5.94645 6.27283 6.53254 5.80063 7.21542 5.5235C7.8983 5.24637 8.6477 5.17659 9.37001 5.32289C10.0923 5.4692 10.7555 5.82509 11.2767 6.34611C11.2805 6.35025 11.2846 6.35416 11.2889 6.35783L12.5348 7.4997H11.25C11.1505 7.4997 11.0552 7.53921 10.9848 7.60954C10.9145 7.67986 10.875 7.77524 10.875 7.8747C10.875 7.97416 10.9145 8.06954 10.9848 8.13987C11.0552 8.21019 11.1505 8.2497 11.25 8.2497H13.5C13.5995 8.2497 13.6948 8.21019 13.7652 8.13987C13.8355 8.06954 13.875 7.97416 13.875 7.8747V5.6247C13.875 5.52524 13.8355 5.42986 13.7652 5.35954C13.6948 5.28921 13.5995 5.2497 13.5 5.2497C13.4005 5.2497 13.3052 5.28921 13.2348 5.35954C13.1645 5.42986 13.125 5.52524 13.125 5.6247V7.02158L11.8008 5.8122C11.1708 5.18466 10.369 4.75783 9.49663 4.58559C8.62425 4.41335 7.72041 4.50343 6.89919 4.84446C6.07797 5.18549 5.37619 5.76217 4.88245 6.50171C4.38871 7.24125 4.12513 8.11049 4.125 8.9997Z" fill="#606060" />
</svg>

);

